"use client";
import { useState } from "react";
import { Box, Container, Link as MuiLink, Typography } from "@mui/material";
import { useAuth } from "../util/auth";
import CustomDialog from "./CustomDialog";
import Section from "./Section";
import SocialLinks from "./SocialLinks";
import handleLeavePlay from "../util/handleLeavePlay";
import { usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import ShareWidget from "./ShareWidget";
import { getMeasurementId, openConsentBanner } from "../lib/analytics";
import { absoluteUrl } from "../lib/seo/siteMetadata";

/** Shared hover-interactive link style used throughout the footer. */
const footerLinkSx = {
  color: "text.secondary",
  fontSize: "clamp(0.7rem, 3.7dvw, 0.875rem)",
  textDecoration: "none",
  cursor: "pointer",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "primary.main",
  },
};

/** Nav link style — slightly more prominent than legal/meta links. */
const navLinkSx = {
  ...footerLinkSx,
  fontWeight: 500,
  color: "text.primary",
  opacity: 0.75,
  transition: "color 0.2s ease, opacity 0.2s ease",
  "&:hover": {
    color: "primary.main",
    opacity: 1,
  },
};

const HIDDEN_PATHS = [
  "/instance",
  "/brain-loading",
];

function Footer(props) {
  const auth = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const clusterParam =
    searchParams.get("cluster") || searchParams.get("cluster_id");
  const [leavePlayPath, setLeavePlayPath] = useState("");

  const linkClick = (targetPath) => {
    if (pathname.startsWith("/play") && clusterParam) {
      setLeavePlayPath(targetPath);
    } else {
      window.location.href = targetPath;
    }
  };

  const isSharePage = pathname.startsWith("/share/");
  const siteHref = (path) => (isSharePage ? absoluteUrl(path) : path);

  if (
    HIDDEN_PATHS.includes(pathname) ||
    pathname.startsWith("/brain-visualizer") ||
    pathname.startsWith("/new-experiment")
  ) {
    return null;
  }

  const backgroundColor =
    process.env.NEXT_PUBLIC_NODE_ENV === "staging"
      ? "rgb(18 55 46)"
      : undefined;

  return (
    <>
      <Section
        size={props.size}
        bgImage={props.bgImage}
        bgImageOpacity={props.bgImageOpacity}
        sx={{
          zIndex: 10,
          mt: props.sticky ? "auto" : undefined,
          py: { xs: 2.5, md: 2 },
          px: { xs: 0, md: "10px" },
          backgroundColor: backgroundColor || "background.dark",
          borderTop: "1px solid",
          borderColor: "background.light",
        }}
      >
        <Container
          sx={{
            "@media (min-width: 1200px)": {
              maxWidth: "none",
            },
            mt: { xs: 2, lg: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
            }}
          >
            {/* Logo + copyright + legal links */}
            <Box
              sx={{
                flex: { xs: "none", md: 1 },
                order: { xs: 3, md: 1 },
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <MuiLink
                href="https://www.neuraville.com"
                target="_blank"
                rel="noreferrer"
                sx={{
                  textDecoration: "none",
                  color: "text.secondary",
                  transition: "color 0.2s ease",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Image
                    src="/img/neuraville-logo.png"
                    alt="Neuraville logo"
                    width={28}
                    height={28}
                    sizes="28px"
                    style={{
                      display: "block",
                      objectFit: "contain",
                      opacity: 0.85,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "clamp(0.7rem, 3.2dvw, 0.8rem)",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: "inherit",
                      }}
                    >
                      Neuraville
                    </Typography>
                    {props.description && (
                      <Typography
                        sx={{
                          fontSize: "clamp(0.6rem, 2.8dvw, 0.7rem)",
                          color: "inherit",
                          lineHeight: 1.2,
                          opacity: 0.7,
                        }}
                      >
                        {props.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </MuiLink>

              {/* Vertical rule separating branding from legal links */}
              <Box
                aria-hidden="true"
                sx={{
                  width: "1px",
                  height: "18px",
                  bgcolor: "background.light",
                  display: { xs: "none", sm: "block" },
                }}
              />

              <Box
                display="flex"
                alignItems="center"
                sx={{ gap: { xs: 1.5, sm: 2 }, flexWrap: "wrap" }}
              >
                <Typography sx={{ ...footerLinkSx, cursor: "default" }}>
                  {props.copyright}
                </Typography>

                {auth.user ? (
                  <>
                    <Typography
                      component="span"
                      onClick={() => linkClick("/legal/terms-of-service")}
                      sx={footerLinkSx}
                    >
                      Terms
                    </Typography>
                    <Typography
                      component="span"
                      onClick={() => linkClick("/legal/privacy-policy")}
                      sx={footerLinkSx}
                    >
                      Privacy
                    </Typography>
                  </>
                ) : (
                  <>
                    <MuiLink href={siteHref("/legal/terms-of-service")} sx={footerLinkSx}>
                      Terms
                    </MuiLink>
                    <MuiLink href={siteHref("/legal/privacy-policy")} sx={footerLinkSx}>
                      Privacy
                    </MuiLink>
                  </>
                )}

                {!isSharePage && getMeasurementId() && (
                  <Typography
                    component="button"
                    onClick={() => openConsentBanner()}
                    sx={{
                      ...footerLinkSx,
                      background: "none",
                      border: "none",
                      padding: 0,
                      font: "inherit",
                    }}
                  >
                    Cookie preferences
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Primary nav links */}
            <Box
              sx={{
                mb: { xs: 2, md: 0 },
                flex: { xs: "none", md: 1 },
                order: { xs: 1, md: 2 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
              }}
            >
              {[
                ...(!auth.user ? [{ label: "Home", href: "/" }] : []),
                { label: "Hub", href: "/hub" },
                { label: "Contact", href: "/contact" },
                { label: "Blog", href: "/blog" },
                { label: "Partners", href: "/partners" },
              ].map(({ label, href }) => (
                <MuiLink
                  key={href}
                  href={siteHref(href)}
                  sx={navLinkSx}
                  onClick={(event) => {
                    if (pathname.startsWith("/play") && clusterParam) {
                      event.preventDefault();
                      linkClick(href);
                    }
                  }}
                >
                  {label}
                </MuiLink>
              ))}
            </Box>

            {/* Social icons + share */}
            <Box
              sx={{
                mb: { xs: 2, md: 0 },
                flex: { xs: "none", md: 1 },
                order: { xs: 2, md: 3 },
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <SocialLinks />
              {!isSharePage && <ShareWidget
                buttonSx={{
                  color: "text.secondary",
                  borderColor: "background.light",
                  transition: "color 0.2s ease, border-color 0.2s ease",
                  "&:hover": {
                    color: "primary.main",
                    borderColor: "primary.main",
                    backgroundColor: "transparent",
                  },
                }}
              />}
            </Box>
          </Box>
        </Container>
      </Section>

      {leavePlayPath && (
        <CustomDialog
          header="Leave this page and end your play session?"
          isOpen={leavePlayPath}
          handleClose={() => setLeavePlayPath("")}
          confirmAction={() =>
            handleLeavePlay(
              auth.user?.accessToken,
              leavePlayPath,
              setLeavePlayPath
            )
          }
          cancelAction={() => setLeavePlayPath("")}
        />
      )}
    </>
  );
}

export default Footer;
